import type React from "react";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useI18n } from "../../lib/i18n";
import { type ValidationError, validateInput } from "../../lib/validation";

/**
 * Interface representing furniture data for CO2 calculation
 */
export interface FurnitureData {
  fte: number; // ETP / FTE
  officeArea: number; // m²
  cabinets: number;
  individualDesks: number;
  officeChairs: number;
  canteenTables: number;
  meetingTables: number;
  visitorChairs: number;
}

/**
 * Props for FurnitureInputForm component
 */
interface FurnitureInputFormProps {
  data: FurnitureData;
  onDataChange: (data: FurnitureData) => void;
}

/**
 * Furniture data input form component
 * Faithfully reproduces the Excel form design
 * With real-time validation and session persistence
 */
const FurnitureInputForm: React.FC<FurnitureInputFormProps> = ({
  data,
  onDataChange,
}) => {
  const { t } = useI18n();
  const [fieldErrors, setFieldErrors] = useState<
    Record<string, ValidationError>
  >({});

  /**
   * Validates and updates a field value
   * @param field - The field to validate and update
   * @param value - The new value to validate
   */
  const validateAndUpdateField = (
    field: keyof FurnitureData,
    value: string,
  ) => {
    const validation = validateInput(field, value);

    // Update errors
    setFieldErrors((prev) => {
      const newErrors = { ...prev };
      if (validation.error) {
        newErrors[field] = validation.error;
      } else {
        delete newErrors[field];
      }
      return newErrors;
    });

    // If validation succeeds, update data
    if (validation.isValid) {
      const newData = { ...data, [field]: validation.sanitizedValue };
      onDataChange(newData);

      // Persist in sessionStorage
      sessionStorage.setItem(
        "transmoove-furniture-data",
        JSON.stringify(newData),
      );
    }
  };

  const furnitureFields = [
    { key: "individualDesks" as const, label: t("furniture.individualDesks") },
    { key: "officeChairs" as const, label: t("furniture.officeChairs") },
    { key: "cabinets" as const, label: t("furniture.cabinets") },
    { key: "canteenTables" as const, label: t("furniture.canteenTables") },
    { key: "meetingTables" as const, label: t("furniture.meetingTables") },
    { key: "visitorChairs" as const, label: t("furniture.visitorChairs") },
  ];

  /**
   * Gets error for a specific field
   */
  const getFieldError = (field: keyof FurnitureData) => fieldErrors[field];

  /**
   * Checks if a field has errors
   */
  const hasError = (field: keyof FurnitureData) => !!fieldErrors[field];

  return (
    <section className="w-full">
      <div className="tm-bar text-transmoove-white px-4 py-1 text-tm-xl font-semibold">
        {t("calculator.section.furniture")}
      </div>

      <div className="px-4 py-3">
        <div className="grid gap-6 lg:grid-cols-[1fr_420px]">
          <div className="space-y-3">
            <div className="grid grid-cols-[1fr_80px_24px] items-center gap-2">
              <Label
                htmlFor="fte"
                className="text-transmoove-text text-tm-base text-right pr-2"
              >
                {t("furniture.fte")}
              </Label>
              <Input
                id="fte"
                type="number"
                min="0"
                step="1"
                value={data.fte || ""}
                onChange={(e) => validateAndUpdateField("fte", e.target.value)}
                className={`h-5 w-20 rounded-none border-black bg-white px-2 py-0 text-tm-base text-center text-transmoove-text focus-visible:ring-0 focus-visible:ring-offset-0 ${
                  hasError("fte") ? "border-red-500 focus:border-red-500" : ""
                }`}
                placeholder="0"
              />
              <span className="text-tm-base text-transmoove-text text-center" />
            </div>
            {hasError("fte") && (
              <p className="text-red-500 text-xs">
                {getFieldError("fte")?.message}
              </p>
            )}

            <div className="grid grid-cols-[1fr_80px_24px] items-center gap-2">
              <Label
                htmlFor="officeArea"
                className="text-transmoove-text text-tm-base text-right pr-2"
              >
                {t("furniture.officeArea")}
              </Label>
              <Input
                id="officeArea"
                type="number"
                min="0"
                step="1"
                value={data.officeArea || ""}
                onChange={(e) =>
                  validateAndUpdateField("officeArea", e.target.value)
                }
                className={`h-5 w-20 rounded-none border-black bg-white px-2 py-0 text-tm-base text-center text-transmoove-text focus-visible:ring-0 focus-visible:ring-offset-0 ${
                  hasError("officeArea")
                    ? "border-red-500 focus:border-red-500"
                    : ""
                }`}
                placeholder="0"
              />
              <span className="text-transmoove-text text-tm-base text-center">
                {t("furniture.squareMeters")}
              </span>
            </div>
            {hasError("officeArea") && (
              <p className="text-red-500">
                {getFieldError("officeArea")?.message}
              </p>
            )}
          </div>

          <div className="space-y-0 lg:justify-self-end lg:pt-1">
            {furnitureFields.map((field) => (
              <div
                key={field.key}
                className="grid grid-cols-[1fr_80px] items-center gap-0"
              >
                <Label
                  htmlFor={field.key}
                  className="text-transmoove-text text-tm-base text-right pr-2"
                >
                  {field.label}
                </Label>
                <div>
                  <Input
                    id={field.key}
                    type="number"
                    min="0"
                    step="1"
                    value={data[field.key] || ""}
                    onChange={(e) =>
                      validateAndUpdateField(field.key, e.target.value)
                    }
                    className={`h-5 w-20 rounded-none border-black bg-white px-2 py-0 text-tm-base text-center text-transmoove-text focus-visible:ring-0 focus-visible:ring-offset-0 ${
                      hasError(field.key)
                        ? "border-red-500 focus:border-red-500"
                        : ""
                    }`}
                    placeholder="0"
                  />
                  {hasError(field.key) && (
                    <p className="text-red-500 mt-1">
                      {getFieldError(field.key)?.message}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {Object.keys(fieldErrors).length > 0 && (
          <div className="mt-3">
            <p className="text-red-600 text-xs font-medium">
              {t("validation.hasErrors", {
                count: Object.keys(fieldErrors).length,
              })}
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default FurnitureInputForm;
